import {ChangeDetectionStrategy, Component, computed, effect, inject, Injectable, input, signal, TemplateRef, ViewChild, viewChild} from "@angular/core";
import {takeUntilDestroyed, toObservable, toSignal} from "@angular/core/rxjs-interop";
import {TranslateModule} from "@ngx-translate/core";
import {Observable, switchMap} from "rxjs";
import {MatTableDataSource, MatTableModule} from "@angular/material/table";
import {MatSort, MatSortModule} from "@angular/material/sort";
import {MatTooltipModule} from "@angular/material/tooltip";
import {GenerellEpostTilMedlemmerSpesifikasjon, GruppeId, GruppeMedlemListeModell, GruppeModell, InvitasjonSlettetRespons, Invitasjonsstatus, LitenIkkeTomStreng, MedlemId, Medlemstatus, SendEpostResultatModell, TilgangsrolleMedlem, GruppeRessursClient} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {GruppemedlemDatatjeneste} from "felles-ng2/tjenester/datalager/GruppemedlemDatatjeneste";
import {distinctUntilChangedDeep} from "felles-ng2/støttefunksjoner/observable";
import {filterNil} from "@ngneat/elf";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {muligeTilgangsrollerForMedlem, primærMedlemsrolle, TilgangsrollerMedlemInfo} from "felles-ng2/modell/medlemModellHjelpere";
import {AvatarKomponent} from "web-ng2/delt/komponenter/avatar/avatar";
import {FormaterMobilnummerPipe} from "felles-ng2/pipes/FormaterMobilnummerPipe";
import {FormaterZonedDateTimePipe} from "felles-ng2/pipes/FormaterZonedDateTimePipe";
import {FormaterPlainDatePipe} from "felles-ng2/pipes/FormaterPlainDatePipe";
import {MatIcon} from "@angular/material/icon";
import {FormaterDatoPeriodeUtenTidPipe} from "felles-ng2/pipes/FormaterDatoPeriodeUtenTidPipe";
import {RammeInnholdKomponent, RammeMedTittelKomponent} from "web-ng2/delt/komponenter/ramme-med-tittel/ramme-med-tittel";
import {EnumOversettelser} from "felles-ng2/tjenester/i18n/EnumOversettelser";
import {MatButton, MatIconButton} from "@angular/material/button";
import {BrukerkontekstTilbyder} from "felles-ng2/tjenester/BrukerkontekstTilbyder";
import {LokaleKomponent} from "web-ng2/delt/komponenter/lokale/lokale";
import {TidssoneKomponent} from "web-ng2/delt/komponenter/tidssone/tidssone";
import {TittelKomponent, TittelVerdiKomponent, VerdiKomponent} from "web-ng2/delt/komponenter/tittel-verdi/tittel-verdi";
import {TittelVerdiSamlingKomponent} from "web-ng2/delt/komponenter/tittel-verdi/tittel-verdi-samling";
import {FormControl, ReactiveFormsModule} from "@angular/forms";
import {MatDivider} from "@angular/material/list";
import {MatMenu, MatMenuItem, MatMenuTrigger} from "@angular/material/menu";
import {MatChipListbox, MatChipOption} from "@angular/material/chips";
import {MedlemRedigerDialogKomponent} from "web-ng2/moduler/medlem/modale/medlem-rediger-dialog/medlem-rediger-dialog";
import {MatDialog} from "@angular/material/dialog";
import {MedlemStengTilgangDialogKomponent} from "web-ng2/moduler/medlem/modale/medlem-stengtilgang-dialog/medlem-stengtilgang-dialog";
import {MedlemGjenåpneTilgangDialogKomponent} from "web-ng2/moduler/medlem/modale/medlem-gjenåpnetilgang-dialog/medlem-gjenåpnetilgang-dialog";
import {MedlemAnonymiserDialogKomponent} from "web-ng2/moduler/medlem/modale/medlem-anonymiser-dialog/medlem-anonymiser-dialog";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {NavigasjonDeaktivering} from "web-ng2/tjenester/NavigasjonDeaktivering";
import {MeldingTilBruker} from "felles/tjenester/MeldingTilBruker";
import {Bekreftelsesdialog} from "web-ng2/tjenester/Bekreftelsesdialog";
import {InlineMeldingKomponent} from "web-ng2/delt/komponenter/inline-melding/inline-melding";
import {MedlemInvitasjonOpprettDialogKomponent} from "web-ng2/moduler/medlem/modale/medlem-invitasjon-opprett-dialog/medlem-invitasjon-opprett-dialog";
import {MedlemInvitasjonRedigerDialogKomponent} from "web-ng2/moduler/medlem/modale/medlem-invitasjon-rediger-dialog/medlem-invitasjon-rediger-dialog";
import {SendEpostDialogKomponent} from "web-ng2/moduler/epost/modale/send-epost-dialog/send-epost-dialog";

const debug = initDebugger('MedlemmerListKomponent');
type MedlemVisningStatus = 'aktiv' | 'invitasjon-ikke-sendt' | 'invitasjon-sendt' | 'invitasjon-avslått' | 'deaktivert' | 'anonymisert';


@Component({
    selector: 'medlemmer-list',
    templateUrl: 'medlemmer-list.html',
    styleUrl: 'medlemmer-list.scss',
    imports: [
        TranslateModule,
        MatTableModule,
        MatSortModule,
        MatTooltipModule,
        AvatarKomponent,
        FormaterMobilnummerPipe,
        FormaterZonedDateTimePipe,
        FormaterPlainDatePipe,
        MatIcon,
        FormaterDatoPeriodeUtenTidPipe,
        RammeInnholdKomponent,
        RammeMedTittelKomponent,
        MatButton,
        LokaleKomponent,
        TidssoneKomponent,
        TittelKomponent,
        TittelVerdiKomponent,
        TittelVerdiSamlingKomponent,
        VerdiKomponent,
        ReactiveFormsModule,
        MatDivider,
        MatMenu,
        MatMenuItem,
        MatIconButton,
        MatMenuTrigger,
        MatChipListbox,
        MatChipOption,
        InlineMeldingKomponent
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MedlemmerListKomponent {
    static ajsSelector = 'medlemmerList';

    @ViewChild('bekreftSlettInvitasjonDialogInnhold') private bekreftSlettInvitasjonDialogInnhold!: TemplateRef<any>;

    filterForm = new FormControl<string[]>(['aktive', 'invitasjoner']);
    filterSignal = toSignal(this.filterForm.valueChanges, {initialValue: this.filterForm.value});


    aktiveValgt = computed(() => this.filterSignal()?.includes('aktive'))
    invitasjonerValgt = computed(() => this.filterSignal()?.includes('invitasjoner'))
    deaktiverteValgt = computed(() => this.filterSignal()?.includes('deaktivert'))
    anonymiserteValgt = computed(() => this.filterSignal()?.includes('anonymisert'))

    gruppe = input<GruppeModell | null>(null);
    valgtMedlemId = input<MedlemId | null>(null);

    displayedColumns: string[] = ['ekspander', 'avatar', 'navn', 'rolle', 'rolleStartdato', 'rolleSluttdato', /*'epost',*/ /*'mobilnummer',*/ 'administrator', 'status', 'meny'];
    dataSource = new MatTableDataSource<GruppeMedlemListeModell>([]);
    sort = viewChild(MatSort);

    private hentedeMedlemmer = signal<GruppeMedlemListeModell[]>([]);

    expandedElement = signal<GruppeMedlemListeModell | null>(null);

    /** Checks whether an element is expanded. */
    isExpanded(element: GruppeMedlemListeModell) {
        return this.expandedElement() === element;
    }

    /** Toggles the expanded state of an element. */
    toggle(element: GruppeMedlemListeModell) {
        this.expandedElement.set(this.isExpanded(element) ? null : element);
    }

    aktiveMedlemmer = computed(() => {
        const aktiveValgt = this.aktiveValgt();
        const invitasjonerValgt = this.invitasjonerValgt();
        const deaktiverteValgt = this.deaktiverteValgt();
        const anonymiserteValgt = this.anonymiserteValgt();
        return this.hentedeMedlemmer().filter((medlem) => aktiveValgt && this.medlemVisnigsStatus(medlem) == 'aktiv' || invitasjonerValgt && this.medlemErInvitasjon(medlem) || deaktiverteValgt && this.medlemVisnigsStatus(medlem) == 'deaktivert' || anonymiserteValgt && this.medlemVisnigsStatus(medlem) == 'anonymisert');
    });

    tilgangsroller = computed(() => this.expandedElement() ? tilgangsrollerInfo(this.expandedElement()) : []);

    redigeringMulig = computed(() => this.kontekst.autentisertBruker().inneharTilgangsrolleIGruppe(TilgangsrolleMedlem.skrive, this.gruppe()!.id));

    antallAktive = computed(() => this.hentedeMedlemmer().filter(medlem => this.medlemVisnigsStatus(medlem) == 'aktiv').length);
    antallInvitasjoner = computed(() => this.hentedeMedlemmer().filter(medlem => this.medlemErInvitasjon(medlem)).length);
    antallDeaktiverte = computed(() => this.hentedeMedlemmer().filter(medlem => this.medlemVisnigsStatus(medlem) == 'deaktivert').length);
    antallAnonymisert = computed(() => this.hentedeMedlemmer().filter(medlem => this.medlemVisnigsStatus(medlem) == 'anonymisert').length);

    private avhengigheter = inject(MedlemmerListKomponentAvhengigheter);

    constructor(private kontekst: BrukerkontekstTilbyder, public enumOversettelser: EnumOversettelser, private matDialog: MatDialog, private navigasjonDeaktivering: NavigasjonDeaktivering, private meldingTilBruker: MeldingTilBruker, private bekreftelsesdialog: Bekreftelsesdialog, private oversettelser: Oversettelser) {
        this.dataSource.sortingDataAccessor = (item: GruppeMedlemListeModell, property: string): string => {
            switch (property) {
                case 'navn':
                    return item.navn ? item.navn.toLowerCase() : '';
                case 'rolle':
                    const rolle = primærMedlemsrolle(item);
                    if (!rolle) return '999'; // uten primærrolle skal sist
                    return rolle.indeks.toString().padStart(3, '0');
                case 'rolleStartdato':
                    return primærMedlemsrolle(item)?.periode.start ?? '';
                case 'rolleSluttdato':
                    return primærMedlemsrolle(item)?.periode.slutt ?? '';
                case 'epost':
                    if (this.medlemVisnigsStatus(item) == 'anonymisert') return '';
                    return item.epost ? item.epost.toLowerCase() : '';
                case 'mobilnummer':
                    return item.mobilnummer ? item.mobilnummer.toLowerCase() : '';
                case 'administrator':
                    return item.tilgangsroller ? item.tilgangsroller.includes(TilgangsrolleMedlem.skrive).toString() : '';
                case 'status':
                    switch (this.medlemVisnigsStatus(item)) {
                        case "aktiv":
                            return '1';
                        case "invitasjon-ikke-sendt":
                            return '2';
                        case "invitasjon-sendt":
                            return '3';
                        case "invitasjon-avslått":
                            return '4';
                        case "deaktivert":
                            return '5';
                        case "anonymisert":
                            return '6';
                    }
                    return 'skalikkeskje'
                default:
                    return (item as any)[property]?.toString()?.toLowerCase() ?? '';
            }
        };

        toObservable(this.gruppe).pipe(
          filterNil(),
          switchMap(gruppe => this.avhengigheter.medlemmer(gruppe.id)),
          takeUntilDestroyed()
        ).subscribe(data => this.hentedeMedlemmer.set(data));

        effect(() => {
            this.dataSource.data = this.aktiveMedlemmer();
        });

        effect(() => {
            const sortInstance = this.sort();
            if (sortInstance) {
                this.dataSource.sort = sortInstance;
            }
        });

        // ekspander valgt medlem dersom det er satt (vi bryr oss ikke om det er med i filter eller ikke)
        effect(() => {
            if (this.valgtMedlemId() != null) {
                const medlem = this.hentedeMedlemmer().find(medlem => medlem.id == this.valgtMedlemId())
                if (medlem)
                    this.expandedElement.set(medlem);
            }
        });
    }

    menyDeaktivert(): boolean {
        return false
    }

    visRedigerKnapp(medlem: GruppeMedlemListeModell): boolean {
        if (!this.redigeringMulig()) return false;
        return this.medlemVisnigsStatus(medlem) == 'aktiv' || this.medlemVisnigsStatus(medlem) == 'invitasjon-ikke-sendt' || this.medlemVisnigsStatus(medlem) == 'invitasjon-sendt';
    }

    visStengTilgangKnapp(medlem: GruppeMedlemListeModell): boolean {
        if (!this.redigeringMulig()) return false;
        if (this.erInnloggetBruker(medlem)) return false;
        return this.medlemVisnigsStatus(medlem) == 'aktiv';
    }

    visGjenaapneTilgangKnapp(medlem: GruppeMedlemListeModell): boolean {
        if (!this.redigeringMulig()) return false;
        return this.medlemVisnigsStatus(medlem) == 'deaktivert';
    }

    visPermanentDeaktivering(medlem: GruppeMedlemListeModell): boolean {
        if (!this.redigeringMulig()) return false;
        return this.medlemVisnigsStatus(medlem) == 'deaktivert';
    }

    visSlettInvitasjon(medlem: GruppeMedlemListeModell): boolean {
        if (!this.redigeringMulig()) return false;
        return this.medlemErInvitasjon(medlem);
    }


    erInnloggetBruker(medlem: GruppeMedlemListeModell): boolean {
        return this.kontekst.autentisertBruker().bruker.id == medlem.brukerId
    }

    rediger(medlem: GruppeMedlemListeModell) {
        if (this.medlemErInvitasjon(medlem)) {
            MedlemInvitasjonRedigerDialogKomponent.vis(this.matDialog, medlem, this.gruppe()!);
        } else {
            MedlemRedigerDialogKomponent.vis(this.matDialog, medlem, this.gruppe()!);
        }
    }

    stengTilgang(medlem: GruppeMedlemListeModell) {
        MedlemStengTilgangDialogKomponent.vis(this.matDialog, medlem, this.gruppe()!);
    }

    gjenaapneTilgang(medlem: GruppeMedlemListeModell) {
        MedlemGjenåpneTilgangDialogKomponent.vis(this.matDialog, medlem);
    }

    permanentDeaktiver(medlem: GruppeMedlemListeModell) {
        MedlemAnonymiserDialogKomponent.vis(this.matDialog, medlem, this.gruppe()!);
    }

    async slettEllerAnonymiser(medlem: GruppeMedlemListeModell) {
        const årsak = await this.bekreftelsesdialog.visSlettMedÅrsak({
            tittel: this.oversettelser.oversett(i18n('Bekreft sletting av invitasjon')),
            htmlInnhold: this.bekreftSlettInvitasjonDialogInnhold,
            htmlInnholdContext: {medlemNavn: medlem.navn},
            avbrytKnapp: this.oversettelser.oversett(i18n('Avbryt')),
            okKnapp: this.oversettelser.oversett(i18n('Ja, slett'))
        }).somPromise();
        if (erNil(årsak)) return;

        const respons = await this.avhengigheter.slettEllerAnonymiser(medlem.id, årsak);
        if (respons.slettet)
            this.meldingTilBruker.infomelding(this.oversettelser.oversett(i18n('Invitasjonen til {navn} har blitt slettet'), {navn: medlem.navn}));
        else
            this.meldingTilBruker.infomelding(this.oversettelser.oversett(i18n('Invitasjonen til {navn} har blitt permanent deaktivert'), {navn: medlem.navn}));
    }

    async sendInvitasjon(medlem: GruppeMedlemListeModell) {
        await this.avhengigheter.sendInvitasjon(medlem.id);
        this.meldingTilBruker.infomelding(this.oversettelser.oversett(i18n('Invitasjon sendt til {navnEllerEpost} / {epost}'), {
            navnEllerEpost: medlem.navn,
            epost: medlem.epost
        }));
    }


    async slettInvitasjon(medlem: GruppeMedlemListeModell) {
        await this.slettEllerAnonymiser(medlem)
    }

    visSendInvitasjonIgjen(medlem: GruppeMedlemListeModell) {
        if (!this.redigeringMulig()) return false;
        return this.medlemVisnigsStatus(medlem) == 'invitasjon-sendt' || this.medlemVisnigsStatus(medlem) == 'invitasjon-avslått';
    }

    visSendInvitasjon(medlem: GruppeMedlemListeModell) {
        if (!this.redigeringMulig()) return false;
        return this.medlemVisnigsStatus(medlem) == 'invitasjon-ikke-sendt';
    }

    sendInvitasjonIgjen(medlem: GruppeMedlemListeModell) {
        this.sendInvitasjon(medlem);
    }


    nyttMedlem() {
        MedlemInvitasjonOpprettDialogKomponent.vis(this.matDialog, this.gruppe()!)
    }

    sendEpostDialog() {
        const valgbareMedlemmer = this.hentedeMedlemmer()
          .filter(medlem => erIkkeNil(medlem.brukerId) && medlem.medlemstatus == Medlemstatus.normal_tilgang)
        SendEpostDialogKomponent.vis(this.matDialog, this.oversettelser.oversett(i18n('E-post til {gruppetittel} i {organisasjon}'), {gruppetittel: this.gruppe()!.tittel, organisasjon: this.gruppe()!.organisasjonsnavn}), valgbareMedlemmer, valgbareMedlemmer, this.sendEpost.bind(this))
    }

    // må matche signatur til metode i sendEpostDialog
    sendEpost(epostTilMedlemmerSpesifikasjon: GenerellEpostTilMedlemmerSpesifikasjon): Promise<SendEpostResultatModell> {
        return this.avhengigheter.sendEpostTilGruppe(this.gruppe()!.id, epostTilMedlemmerSpesifikasjon);
    }

    medlemVisnigsStatus(medlem: GruppeMedlemListeModell): MedlemVisningStatus {
        if (medlem.anonymisert) return 'anonymisert';
        if (!medlem.harTilgang) return 'deaktivert';
        if (medlem.invitasjonsstatus == Invitasjonsstatus.ikke_sendt) return 'invitasjon-ikke-sendt';
        if (medlem.invitasjonsstatus == Invitasjonsstatus.sendt) return 'invitasjon-sendt';
        if (medlem.invitasjonsstatus == Invitasjonsstatus.avslått) return 'invitasjon-avslått';
        return 'aktiv';
    }

    medlemErInvitasjon(medlem: GruppeMedlemListeModell): boolean {

        switch (this.medlemVisnigsStatus(medlem)) {
            case 'invitasjon-ikke-sendt':
            case 'invitasjon-sendt':
            case 'invitasjon-avslått':
                return true;
        }
        return false;
    }

    protected readonly TilgangsrolleMedlem = TilgangsrolleMedlem;
    protected readonly Invitasjonsstatus = Invitasjonsstatus;
}

@Injectable({providedIn: 'root'})
export class MedlemmerListKomponentAvhengigheter {
    constructor(private gruppemedlemDatatjeneste: GruppemedlemDatatjeneste, private gruppeRessurs: GruppeRessursClient) {
    }

    medlemmer(id: GruppeId): Observable<GruppeMedlemListeModell[]> {
        return this.gruppemedlemDatatjeneste.finnTilknyttet(id).pipe(distinctUntilChangedDeep());
    }

    async slettEllerAnonymiser(id: MedlemId, årsak: LitenIkkeTomStreng): Promise<InvitasjonSlettetRespons> {
        return this.gruppemedlemDatatjeneste.slettEllerAnonymiserInvitasjon(id, {aarsak: årsak});
    }

    async sendInvitasjon(id: MedlemId) {
        await this.gruppemedlemDatatjeneste.sendInvitasjon(id);
    }

    sendEpostTilGruppe(gruppeId: GruppeId, spesifikasjon: GenerellEpostTilMedlemmerSpesifikasjon): Promise<SendEpostResultatModell> {
        return this.gruppeRessurs.sendEpost(gruppeId, spesifikasjon).somPromise();
    }

}

function tilgangsrollerInfo(medlem: GruppeMedlemListeModell | null): TilgangsrollerMedlemInfo[] {
    return erNil(medlem) ? [] :
      muligeTilgangsrollerForMedlem()
        .filter(rolleInfo => medlem.tilgangsroller.includes(rolleInfo.rolle))
}

